import { useEffect, useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { endOfDay, formatISO, startOfDay, subDays } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import { formatErrorMessage } from "@APP/utils";
import { AppState, hideLoader, setOrganisationSearchResults, showLoader } from "@APP/redux";
import { NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION, commonFormatDate } from "@APP/constants";
import { Page, ScreenHeader } from "@APP/components";
import { filterToolbarStyles } from "@APP/styles";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { API } from "@APP/services";
import { User } from "@APP/types";

import OrganisationList from "./OrganisationList";

const DATE_PRESETS = ["Today", "Last 7 days", "Last 30 days", "Custom"];

const rangeFor = (label: string) => {
  const now = new Date();
  if (label === "Last 7 days") return { from: formatISO(subDays(now, 7)), to: formatISO(now) };
  if (label === "Last 30 days") return { from: formatISO(subDays(now, 30)), to: formatISO(now) };

  return { from: formatISO(startOfDay(now)), to: formatISO(now) };
};

export interface Organisation {
  orgId: string;
  orgName: string;
  status: string;
  subscriptionStatus: string;
  createdAt?: string;
  users: User[];
  order?: string;
}

export interface OrganisationData {
  data: Organisation[];
  meta?: {
    totalItems: number;
  };
}

const OrganisationDashboardView = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const classes = filterToolbarStyles();

  const [fromLabel, setFromLabel] = useState("Today");

  const { from, to } = useSelector(
    ({
      userAdmin: {
        organisationDashboard: { from, to },
      },
    }: AppState) => ({
      from,
      to,
    }),
  );

  const [entries, setEntries] = useState(25);
  const [page, setPage] = useState(0);
  const [organisationData, setOrganisationData] = useState<OrganisationData>();
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const [submitFrom, setSubmitFrom] = useState("");
  const [submitTo, setSubmitTo] = useState("");
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [lastPage, setLastPage] = useState(0);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const handleErrorCodes = useHandleErrorCodes();

  const handleFetchOrganisations = async ({ from, to }: { from: string; to: string }) => {
    const utcFrom = zonedTimeToUtc(startOfDay(new Date(from)), "UTC").toISOString();
    const utcTo = zonedTimeToUtc(endOfDay(new Date(to)), "UTC").toISOString();

    try {
      let sort_by = "";
      switch (orderBy) {
        case "orgName":
          sort_by = "org_name";
          break;
        case "status":
          sort_by = "status";
          break;
        case "createdAt":
        case "createdTime":
          sort_by = "created_at";
          break;
        case "subscriptionStatus":
          sort_by = "subscription_status";
          break;
        default:
          break;
      }
      dispatch(showLoader());
      const orgData: OrganisationData = await API.getSearchedOrganisations(
        {
          from: utcFrom,
          to: utcTo,
        },
        { page: page, entries: entries },
        `sort_by=${sort_by}&sort_type=${order}`,
      );
      if (orgData?.meta?.totalItems) {
        if (orgData.meta.totalItems <= entries) {
          setLastPage(1);
        } else {
          const lastPageIndex = Math.ceil(orgData.meta.totalItems / entries);
          setLastPage(lastPageIndex);
        }
      }

      setOrganisationData(orgData);
      dispatch(setOrganisationSearchResults(orgData));
    } catch (error: any) {
      const errorData = error?.response?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);

      if (!isHandled) return;
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    const dateString = new Date().toISOString();
    handleFetchOrganisations({ from: submitFrom || dateString, to: submitTo || dateString });
  }, [entries, page, order, orderBy]);

  const handleOnSubmit = ({ from, to }: { from: string; to: string }) => {
    handleFetchOrganisations({ from, to });
    setSubmitFrom(from);
    setSubmitTo(to);
    setPage(0);
  };

  const { errors, handleSubmit, touched, values, setFieldValue } = useFormik({
    initialValues: {
      from: zonedTimeToUtc(from, "UTC").toISOString(),
      to: zonedTimeToUtc(to, "UTC").toISOString(),
    },
    onSubmit: handleOnSubmit,
  });

  const handleOnEntriesChange = (entries: number) => {
    setPage(NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION);
    setEntries(entries);
  };

  useEffect(() => {
    const from = formatISO(startOfDay(new Date()));
    const to = formatISO(new Date());
    const utcFrom = zonedTimeToUtc(from, "UTC").toISOString();
    const utcTo = zonedTimeToUtc(to, "UTC").toISOString();
    handleFetchOrganisations({ from: utcFrom, to: utcTo });
  }, []);

  return (
    <>
      <Page title="Organisation Dashboard">
        <ScreenHeader title="Organisation Dashboard" />
        <form onSubmit={handleSubmit}>
          <Box className={classes.toolbar}>
            <Box className={classes.topRow}>
              <div className={classes.segment} role="group" aria-label="Created date range">
                {DATE_PRESETS.map((label) => {
                  const isCustom = label === "Custom";

                  return (
                    <button
                      key={label}
                      type={isCustom ? "button" : "submit"}
                      aria-pressed={fromLabel === label}
                      className={`${classes.segmentButton} ${
                        fromLabel === label ? classes.segmentButtonActive : ""
                      }`}
                      onClick={() => {
                        const { from, to } = rangeFor(label);
                        setFieldValue("from", from);
                        setFieldValue("to", to);
                        setFromLabel(label);
                        setCustomDatePicker(isCustom);
                      }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </Box>

            {customDatePicker && (
              <Box className={classes.customRange}>
                <DatePicker
                  label="From"
                  value={values.from}
                  inputFormat={commonFormatDate}
                  disableFuture
                  open={startDatePickerOpen}
                  onOpen={() => setStartDatePickerOpen(true)}
                  onClose={() => setStartDatePickerOpen(false)}
                  onChange={(value) => setFieldValue("from", value)}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      size="small"
                      onClick={() => setStartDatePickerOpen(true)}
                      error={Boolean(touched.from && errors.from)}
                      helperText={touched.from && errors.from}
                    />
                  )}
                />
                <DatePicker
                  label="To"
                  value={values.to}
                  inputFormat={commonFormatDate}
                  disableFuture
                  open={endDatePickerOpen}
                  onOpen={() => setEndDatePickerOpen(true)}
                  onClose={() => setEndDatePickerOpen(false)}
                  onChange={(value) => setFieldValue("to", value)}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      size="small"
                      onClick={() => setEndDatePickerOpen(true)}
                      error={Boolean(touched.to && errors.to)}
                      helperText={touched.to && errors.to}
                    />
                  )}
                />
                <Button type="submit" variant="contained" color="primary">
                  Apply
                </Button>
              </Box>
            )}
          </Box>
          <OrganisationList
            data={(organisationData as OrganisationData) || []}
            entries={entries}
            setEntries={setEntries}
            page={page}
            lastPage={lastPage}
            setPage={setPage}
            handleOnEntriesChange={handleOnEntriesChange}
            order={order}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
          />
        </form>
      </Page>
    </>
  );
};

export default OrganisationDashboardView;
